import type { Dict } from "./uk.ts";

export type Formatters = ReturnType<typeof createFormatters>;

/**
 * Number, money, size, duration, and date formatting for one language.
 * Unit words come from the dictionary; month names and digit grouping come from `Intl`.
 */
export function createFormatters(t: Dict) {
  const locale = t.meta.locale;
  const units = t.units;
  const spaced = locale.startsWith("uk");
  const unit = (value: string | number, word: string) => (spaced ? `${value} ${word}` : `${value}${word}`);

  const integer = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  const decimal1 = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const compactFmt = new Intl.NumberFormat(locale, { notation: "compact", maximumFractionDigits: 1 });
  const dateFmt = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" });
  const dateShortFmt = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" });
  const dateTimeFmt = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
  const fullDateTimeFmt = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const timeFmt = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" });
  const preciseTimeFmt = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const monthFmt = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" });
  const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short" });

  const parse = (iso: string | null | undefined): Date | null => {
    if (!iso) return null;
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  /** "3 хв 12 с" / "3m 12s". */
  const duration = (ms: number | null | undefined): string => {
    if (ms === null || ms === undefined || !Number.isFinite(ms)) return "—";
    const totalSeconds = Math.max(0, Math.round(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${unit(hours, units.hour)} ${unit(minutes, units.minute)}`;
    if (minutes > 0) return `${unit(minutes, units.minute)} ${unit(seconds, units.second)}`;
    return unit(seconds, units.second);
  };

  return {
    locale,
    number: (value: number) => integer.format(value),
    /** Exact below 10 000, compact above: 9 870, 12.4K. */
    compact: (value: number) => (Math.abs(value) < 10_000 ? integer.format(value) : compactFmt.format(value)),
    decimal: (value: number) => decimal1.format(value),
    percent: (fraction: number) => `${Math.round(fraction * 100)}%`,
    /** Dollars, as the spec writes them in both languages: $3.12. `precise` keeps small amounts readable. */
    usd: (value: number, options: { precise?: boolean; whole?: boolean } = {}) => {
      const abs = Math.abs(value);
      const sign = value < 0 ? "−" : "";
      if (options.whole && Number.isInteger(value)) {
        return `${sign}$${abs.toLocaleString("en-US")}`;
      }
      if (!options.precise && abs > 0 && abs < 0.01) return "<$0.01";
      const digits = options.precise ? (abs > 0 && abs < 0.1 ? 4 : abs < 1 ? 3 : 2) : 2;
      return `${sign}$${abs.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
    },
    duration,
    /** Live timer: 03:12, or 1:03:12 past an hour. */
    clock: (ms: number) => {
      const totalSeconds = Math.max(0, Math.floor(ms / 1000));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const mm = String(minutes).padStart(2, "0");
      const ss = String(seconds).padStart(2, "0");
      return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
    },
    /** A step's duration: "4,2 с" / "4.2 s"; minutes past a minute. */
    stepDuration: (ms: number) => {
      if (ms < 60_000) {
        const seconds = ms / 1000;
        const value = seconds < 10 ? decimal1.format(seconds) : integer.format(Math.round(seconds));
        return `${value} ${units.second}`;
      }
      return duration(ms);
    },
    bytes: (bytes: number | null | undefined) => {
      if (bytes === null || bytes === undefined) return "—";
      if (bytes < 1024) return `${integer.format(bytes)} ${units.byte}`;
      if (bytes < 1024 * 1024) return `${integer.format(Math.round(bytes / 1024))} ${units.kilobyte}`;
      return `${decimal1.format(bytes / (1024 * 1024))} ${units.megabyte}`;
    },
    date: (iso: string | null | undefined) => {
      const date = parse(iso);
      return date ? dateFmt.format(date) : "—";
    },
    dateShort: (iso: string | null | undefined) => {
      const date = parse(iso);
      return date ? dateShortFmt.format(date) : "—";
    },
    dateTime: (iso: string | null | undefined) => {
      const date = parse(iso);
      return date ? dateTimeFmt.format(date) : "—";
    },
    fullDateTime: (iso: string | null | undefined) => {
      const date = parse(iso);
      return date ? fullDateTimeFmt.format(date) : "—";
    },
    time: (iso: string | null | undefined) => {
      const date = parse(iso);
      return date ? timeFmt.format(date) : "—";
    },
    preciseTime: (iso: string | null | undefined) => {
      const date = parse(iso);
      return date ? preciseTimeFmt.format(date) : "—";
    },
    weekday: (iso: string) => {
      const date = parse(iso);
      return date ? weekdayFmt.format(date) : "—";
    },
    /** `YYYY-MM` as "листопад 2026 р." / "November 2026". */
    month: (month: string) => {
      const [year, index] = month.split("-").map(Number);
      const label = monthFmt.format(new Date(year ?? 2000, (index ?? 1) - 1, 1));
      return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
    },
  };
}
